"use client";

import React, { useState, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
    SparklesIcon,
    XMarkIcon,
    MinusIcon,
    ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import AIDashboard from "./AIDashboard";

const FloatingAIWidget: React.FC = () => {
    const pathname = usePathname();
    const router = useRouter();
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [showNotification, setShowNotification] = useState(false);

    const hiddenPaths = [
        "/ai-assistant",
        "/instructors",
        "/admin",
        "/login",
        "/signup",
    ];

    const shouldShow = !hiddenPaths.some((path) => pathname?.startsWith(path));

    useEffect(() => {
        const lastVisit = localStorage.getItem("ai_widget_notified");
        if (!lastVisit && shouldShow) {
            const timer = setTimeout(() => {
                setShowNotification(true);
                localStorage.setItem("ai_widget_notified", Date.now().toString());
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [shouldShow]);

    if (!shouldShow) return null;

    // Hide floating widget completely
    return null;

    return (
        <div className={`fixed z-[9999] hidden md:flex flex-col items-end gap-4 zoom-container ${
            isOpen
                ? "bottom-20 left-4 right-4 sm:bottom-6 sm:left-auto sm:right-6"
                : "bottom-20 right-4 sm:bottom-6 sm:right-6"
        }`}>

            {/* Chat Window */}
            {isOpen && (
                <div
                    className={`bg-white shadow-2xl border border-gray-100 overflow-hidden transition-all duration-300 flex flex-col w-full sm:origin-bottom-right rounded-2xl ${
                        isMinimized
                            ? "h-14 sm:w-72"
                            : "h-[calc(100dvh-9rem)] sm:h-[580px] sm:w-[420px]"
                    }`}
                >
                    {/* Header */}
                    <div className="bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between shrink-0">
                        <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center shadow-sm">
                                <SparklesIcon className="h-4 w-4 text-orange-400" />
                            </div>
                            <div>
                                <span className="font-bold text-sm text-slate-900 leading-tight block">Prana AI</span>
                                {!isMinimized && (
                                    <span className="text-[11px] text-slate-400 leading-tight">Your wellness coach</span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            {/* Open full page */}
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    router.push("/ai-assistant");
                                }}
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-orange-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                                title="Open full Prana AI page"
                            >
                                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                {/* <span className="hidden sm:inline">Full page</span> */}
                            </button>
                            <button
                                onClick={() => setIsMinimized(!isMinimized)}
                                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                                title={isMinimized ? "Expand" : "Minimize"}
                            >
                                <MinusIcon className="h-4 w-4" />
                            </button>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* AIDashboard in compact mode */}
                    {!isMinimized && (
                        <div className="flex-1 min-h-0 overflow-hidden p-3">
                            <AIDashboard compact />
                        </div>
                    )}
                </div>
            )}

            {/* Floating Button */}
            {!isOpen && (
                <div className="relative self-end">
                    {/* Notification Toast */}
                    {/* {showNotification && (
                        <div className="absolute bottom-full right-0 mb-4 w-72 bg-white rounded-2xl shadow-2xl border border-orange-100 p-5 animate-in slide-in-from-bottom-2 duration-500">
                            <div className="flex items-center gap-1.5 mb-2">
                                <span className="text-[10px] font-black text-orange-600 uppercase tracking-widest">Prana AI</span>
                                <div className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                            </div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowNotification(false);
                                }}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-50 rounded-full transition-colors"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                            <p className="text-sm text-gray-700 leading-relaxed">
                                Hi! I&apos;m <span className="font-bold text-gray-900">Prana</span>, your AI wellness guide. Need help with your wellness routine?
                            </p>
                            <button
                                onClick={() => {
                                    setIsOpen(true);
                                    setShowNotification(false);
                                }}
                                className="mt-3 w-full py-2 bg-orange-50 text-orange-600 rounded-lg text-xs font-bold hover:bg-orange-600 hover:text-white transition-all duration-300"
                            >
                                Chat with Prana
                            </button>
                        </div>
                    )} */}

                    <button
                        onClick={() => {
                            setIsOpen(true);
                            setShowNotification(false);
                            setIsMinimized(false);
                        }}
                        className="group relative flex items-center gap-2.5 pl-3 pr-5 py-2.5 bg-white/90 backdrop-blur-md border border-orange-100 rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:shadow-[0_8px_30px_rgba(255,112,67,0.2)] hover:border-orange-300 transition-all duration-500 active:scale-95"
                    >
                        <div className="absolute inset-0 bg-orange-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="relative w-10 h-10 sm:w-11 sm:h-11 bg-gray-900 rounded-full flex items-center justify-center text-white shadow-lg group-hover:bg-orange-600 transition-all duration-500 group-hover:rotate-12">
                            <SparklesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400 group-hover:text-white" />
                        </div>
                        <div className="flex flex-col items-start leading-tight relative">
                            <span className="text-xs sm:text-sm font-bold text-orange-600 mb-0.5">Prana AI</span>
                            <span className="text-xs sm:text-sm font-medium text-gray-800">Ask your guide</span>
                        </div>
                        <div className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 bg-orange-500 rounded-full border-2 border-white flex items-center justify-center">
                            <div className="h-1 w-1 bg-white rounded-full animate-pulse" />
                        </div>
                    </button>
                </div>
            )}
        </div>
    );
};

export default FloatingAIWidget;
